import { PounceStyleProps } from './pounceTypes';

// Extract pounce props from component props
export const extractPounceProps = <T extends Record<string, any>>(
  props: T,
): { pounceProps: Partial<PounceStyleProps>; otherProps: Omit<T, keyof PounceStyleProps> } => {
  const pounceKeys: (keyof PounceStyleProps)[] = [
    // Space
    'm',
    'margin',
    'mt',
    'marginTop',
    'mr',
    'marginRight',
    'mb',
    'marginBottom',
    'ml',
    'marginLeft',
    'mx',
    'marginX',
    'my',
    'marginY',
    'p',
    'padding',
    'pt',
    'paddingTop',
    'pr',
    'paddingRight',
    'pb',
    'paddingBottom',
    'pl',
    'paddingLeft',
    'px',
    'paddingX',
    'py',
    'paddingY',

    // Colors
    'color',
    'backgroundColor',
    'bg',
    'opacity',

    // Typography
    'fontFamily',
    'fontSize',
    'fontWeight',
    'lineHeight',
    'letterSpacing',
    'textAlign',
    'fontStyle',

    // Layout
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'size',
    'display',
    'verticalAlign',
    'overflow',
    'overflowX',
    'overflowY',

    // Background
    'background',
    'backgroundImage',
    'backgroundSize',
    'backgroundPosition',
    'backgroundRepeat',

    // Flexbox
    'alignItems',
    'alignContent',
    'justifyItems',
    'justifyContent',
    'flexWrap',
    'flexDirection',
    'flex',
    'flexGrow',
    'flexShrink',
    'flexBasis',
    'justifySelf',
    'alignSelf',
    'order',

    // Grid
    'gridGap',
    'gridRowGap',
    'gridColumnGap',
    'gap',
    'rowGap',
    'columnGap',
    'gridRow',
    'gridColumn',
    'gridArea',
    'gridAutoFlow',
    'gridAutoRows',
    'gridAutoColumns',
    'gridTemplateRows',
    'gridTemplateColumns',
    'gridTemplateAreas',

    // Border
    'border',
    'borderX',
    'borderY',
    'borderWidth',
    'borderStyle',
    'borderColor',
    'borderRadius',
    'borderTop',
    'borderTopWidth',
    'borderTopStyle',
    'borderTopColor',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderRight',
    'borderRightWidth',
    'borderRightStyle',
    'borderRightColor',
    'borderBottom',
    'borderBottomWidth',
    'borderBottomStyle',
    'borderBottomColor',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'borderLeft',
    'borderLeftWidth',
    'borderLeftStyle',
    'borderLeftColor',

    // Position
    'position',
    'top',
    'right',
    'bottom',
    'left',

    // Shadow
    'boxShadow',
    'textShadow',

    // Custom pounce props
    'shadow',
    'zIndex',
    'fill',
    'stroke',
    'textDecoration',
    'textTransform',
    'animation',
    'transform',
    'visibility',
    'whiteSpace',
    'pointerEvents',
    'wordWrap',
    'wordBreak',
    'overflowWrap',
    'textOverflow',
    'cursor',
    'resize',
    'transition',
    'objectFit',
    'outline',
    'userSelect',
    'transformOrigin',
    'willChange',
    'borderCollapse',
    'borderSpacing',
    'tableLayout',
    'gap',
    'columnCount',

    // Pounce utility props
    'truncated',
    'sx',
    'visuallyHidden',
    'backgroundOpacity',
    'borderOpacity',

    // Pseudo-selector props
    '_after',
    '_before',
    '_focus',
    '_hover',
    '_active',
    '_pressed',
    '_selected',
    '_focusWithin',
    '_invalid',
    '_disabled',
    '_grabbed',
    '_empty',
    '_expanded',
    '_checked',
    '_mixed',
    '_odd',
    '_even',
    '_visited',
    '_readOnly',
    '_first',
    '_last',
    '_groupHover',
    '_notFirst',
    '_notLast',
    '_placeholder',
    '_autofill',
  ];

  const pounceProps: any = {};
  const otherProps: any = { ...props };

  pounceKeys.forEach(key => {
    if (key in props) {
      pounceProps[key] = props[key];
      delete otherProps[key];
    }
  });

  return {
    pounceProps,
    otherProps,
  };
};
